/**
 * Knowledge — connect scattered knowledge sources per project and let AI
 * give reliable, source-grounded answers.
 *
 * Built on shipped functionality: knowledge sources + embeddings/chunks,
 * project/codebase knowledge scoping (src/lib/knowledge-scope.ts), grounded
 * answering, knowledge_scopes visibility (public/internal/restricted) and
 * the hard-walled website-chat widget. A knowledge project needs at least
 * one source — never artificially a codebase or a support desk; intake is
 * optional (only when a chat/question channel is wanted).
 */
import type { UseCaseDefinition } from "../types";

export const knowledgeUseCase: UseCaseDefinition = {
  id: "knowledge",
  version: 1,
  labelKey: "useCases.knowledge.label",
  shortDescriptionKey: "useCases.knowledge.short",
  fullDescriptionKey: "useCases.knowledge.full",
  primaryOutcomeKey: "useCases.knowledge.outcome",
  availability: "available",
  applicableWorkProfiles: ["support", "manager_reviewer", "admin", "custom"],
  requiredCapabilities: ["knowledge.answer"],
  recommendedCapabilities: ["intake.receive", "notifications.post"],
  suggestedBindingPurposes: ["intake"],
  defaultExecutionMode: "read_only",
  defaultRemediationStartMode: "disabled",
  onboardingStepIds: [
    "knowledge.ask-test-question",
    "knowledge.view-sources-used",
    "knowledge.add-missing-source",
  ],
  dashboardSectionIds: [
    "knowledge.sources",
    "knowledge.status",
    "knowledge.questions",
    "knowledge.gaps",
    "knowledge.publication",
    "shared.activity",
  ],
  readinessCheckIds: [
    "knowledge.source",
    "knowledge.indexing",
    "knowledge.scope",
    "knowledge.intake-channel",
  ],
  defaultGovernanceProfileId: "balanced",
  prerequisiteDescriptionKeys: ["useCases.knowledge.prereq.source"],
};
